// The link in an invitation email: /invitation/<id>. Better Auth only shows and accepts it for the
// account with the invited email.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, meQuery } from '../api';
import { authClient } from '../auth-client';
import { Button, Message, Page } from '../ui';

export function InvitationPage() {
  const { t } = useTranslation();
  const { id } = useParams({ from: '/invitation/$id' });
  const queryClient = useQueryClient();
  const me = useQuery(meQuery);
  const invitation = useQuery({
    queryKey: ['invitation', id],
    enabled: Boolean(me.data),
    retry: false,
    // Once accepted it no longer exists: never ask again while the page is open.
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: async () => {
      const { data, error } = await authClient.organization.getInvitation({ query: { id } });
      if (error) throw new ApiError(error.status);
      return data;
    },
  });
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function accept() {
    setBusy(true);
    setError(null);
    // Accepting also makes it the session's active organisation.
    const { error: failure } = await authClient.organization.acceptInvitation({ invitationId: id });
    setBusy(false);
    if (failure) {
      setError(t('invitation.invalid'));
      return;
    }
    await queryClient.invalidateQueries({ queryKey: meQuery.queryKey });
    setAccepted(true);
  }

  let body: ReactNode;
  if (me.isPending || (me.data && invitation.isPending)) {
    body = <p role="status">{t('app.loading')}</p>;
  } else if (!me.data) {
    const redirect = `/invitation/${id}`;
    body = (
      <>
        <p>{t('invitation.loginFirst')}</p>
        <p className="mt-6 flex flex-wrap gap-x-6 gap-y-2">
          <Link to="/login" search={{ redirect }}>
            {t('invitation.login')}
          </Link>
          <Link to="/signup" search={{ redirect }}>
            {t('invitation.signup')}
          </Link>
        </p>
      </>
    );
  } else if (accepted) {
    body = (
      <>
        <Message tone="ok">{t('invitation.accepted')}</Message>
        <p className="mt-6">
          <Link to="/">{t('invitation.continue')}</Link>
        </p>
      </>
    );
  } else if (!invitation.data) {
    body = <Message tone="error">{t('invitation.invalid')}</Message>;
  } else {
    body = (
      <div className="flex flex-col items-start gap-6">
        {error && <Message tone="error">{error}</Message>}
        <p>
          {t('invitation.details', {
            inviter: invitation.data.inviterEmail,
            organization: invitation.data.organizationName,
            role: t(`home.roles.${invitation.data.role}`),
          })}
        </p>
        <Button onPress={accept} isDisabled={busy}>
          {t('invitation.accept')}
        </Button>
      </div>
    );
  }

  // A title of its own after accepting, so the focus moves to it (the button is gone).
  return (
    <Page
      title={t(accepted ? 'invitation.acceptedTitle' : 'invitation.title')}
      brand={!me.data?.organization}
    >
      {body}
    </Page>
  );
}
